import { Flex, Switch } from '@chakra-ui/react'
import { organizationListContext } from 'BrokerTsLib/src/context'
import type {
  OrganizationUnitGridItem,
  OrganizationUnitPagedQuery,
} from 'BrokerTsLib/src/dtos'
import type { FlexGroupOrganization } from 'BrokerTsLib/src/dtos/FlexGroup/FlexGroupOrganization'
import type { Ref } from 'react'
import { forwardRef, useState } from 'react'

import { SectionHeading } from '@/components/Section/SectionHeading'
import { TableSection } from '@/components/Section/TableSection'

import type { FormSectionProps } from '../forms/FormSection'
import {
  OrganizationListCityColumn,
  OrganizationListDescriptionColumn,
  OrganizationListNameColumn,
  OrganizationListTypeColumn,
  OrganizationListValidColumn,
  OrganizationsList,
} from '../organization/OrganizationList'
import { OrganizationListFilter } from '../organization/OrganizationListFilter'
import { AddFlexGroupOrganization } from './AddFlexGroupOrganization'
import { RemoveButton } from './RemoveButton'

interface FlexGroupOrganizationListSectionProps extends FormSectionProps {
  title: string
  flexGroupOrganizations: FlexGroupOrganization[]
  isLoading: boolean
  addOrganizationAsync: (organizationUnitId: number, includeSubordinate: boolean) => Promise<any>
  updateOrganizationAsync?: (organizationUnitId: number, includeSubordinate: boolean) => Promise<any>
  removeOrganizationAsync: (organizationUnitId: number) => Promise<any>
}

export const FlexGroupOrganizationListSection = forwardRef(
  function FlexGroupMemberOrganizationListSection(
    {
      title,
      flexGroupOrganizations,
      isLoading,
      addOrganizationAsync,
      updateOrganizationAsync,
      removeOrganizationAsync,
      ...rest
    }: FlexGroupOrganizationListSectionProps,
    ref: Ref<HTMLDivElement>,
  ) {
    const [filter, setFilter] = useState<OrganizationUnitPagedQuery>({
      showInvalid: false,
      showRootOnly: true,
      pageIndex: 1,
    })

    const organizationsFilter = (item: OrganizationUnitGridItem) => {
      if (!item.valid && !filter.showInvalid) return false
      if (
        filter.name &&
        !(
          item.name?.toLowerCase().includes(filter.name.toLowerCase()) ||
          item.contactName?.toLowerCase().includes(filter.name.toLowerCase()) ||
          item.description?.toLowerCase().includes(filter.name.toLowerCase()) ||
          item.addressCity?.toLowerCase().includes(filter.name.toLowerCase())
        )
      )
        return false

      return true
    }

    const organizations = flexGroupOrganizations.map((x) => x.organizationUnit)

    const filteredItems = organizations.filter(organizationsFilter)

    const columnsDefinitions = [
      OrganizationListNameColumn,
      OrganizationListCityColumn,
      OrganizationListDescriptionColumn,
      OrganizationListValidColumn,
      OrganizationListTypeColumn,
    ]

    if (updateOrganizationAsync) {
      columnsDefinitions.push({
        key: 'includeSubordinate',
        name: 'Zahrnout podřízené',
        cellExpr: (item) => (
          <Switch isChecked={flexGroupOrganizations.find(x => x.organizationUnit.id === item.id)?.includeSubordinate}
            onChange={(e) => updateOrganizationAsync?.(item.id, e.target.checked)} size='md' />
        ),
        cellProps: { textAlign: 'right' },
        headerCellProps: { textAlign: 'right' },
      })
    }

    columnsDefinitions.push({
      key: 'actions',
      name: '',
      cellExpr: (item) => <RemoveButton onClick={() => removeOrganizationAsync(item.id)} />,
      cellProps: { p: 2 },
    })

    return (
      <TableSection
        ref={ref}
        sectionTitle={
          <Flex
            justifyContent={'space-between'}
            alignItems={'start'}
            flexDir={{ base: 'column', sm: 'row' }}
          >
            <SectionHeading>{title}</SectionHeading>
            <AddFlexGroupOrganization
              onAddMember={addOrganizationAsync}
              alreadyAdded={organizations}
            />
          </Flex>
        }
        wide
        {...rest}
      >
        <organizationListContext.Provider
          value={{
            loading: isLoading,
            totalCount: organizations?.length ?? 0,
            currentPage: filter.pageIndex,
            pageSize: organizations?.length ?? 0,
            filter,
            setCurrentPage: () => { },
            setPageSize: () => { },
            updateFilter: setFilter,
            reload: () => { },
          }}
        >
          <OrganizationListFilter disableShowRootOnly p={4} />
          <OrganizationsList
            organizations={filteredItems}
            columnsDefinition={columnsDefinitions}
          />
        </organizationListContext.Provider>
      </TableSection>
    )
  },
)